import { type ApiSubscriber, type RegisteredApi } from './api-registry.types';

/**
 * Keyed by `reducerPath` so registration is idempotent: a module barrel imported
 * from both `store.config.ts` and a component would otherwise mount the same
 * reducer twice, and RTK would warn about it exactly once, in a console nobody
 * is watching.
 */
const apis = new Map<string, RegisteredApi>();

/** Keyed the same way, so a re-registered api replaces its subscriber too. */
const subscribers = new Map<string, ApiSubscriber>();

/**
 * Every RTK Query api calls this at the BOTTOM OF ITS OWN FILE (W4).
 *
 * That is what lets `createAppStore` build its reducer map from whatever has
 * been imported, and it is why adding a module never edits the store: the
 * `store.config.ts` bridge imports the module for its side effect, and the
 * module registers itself. `shared/` still imports nothing from a module (W2).
 */
export function registerApi(api: RegisteredApi, options: { subscribe?: ApiSubscriber } = {}): void {
  apis.set(api.reducerPath, api);

  if (options.subscribe === undefined) {
    subscribers.delete(api.reducerPath);
  } else {
    subscribers.set(api.reducerPath, options.subscribe);
  }
}

/** Registration order — the reducer map and middleware chain follow it. */
export function getRegisteredApis(): RegisteredApi[] {
  return [...apis.values()];
}

/** The bus subscriptions the registered apis asked for, in registration order. */
export function getRegisteredSubscribers(): ApiSubscriber[] {
  return [...subscribers.values()];
}

/** Test-only: one spec's fake apis must not leak into another's store. */
export function clearApis(): void {
  apis.clear();
  subscribers.clear();
}
